// App.tsx
import { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
  useLocation,
} from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { AuthProvider } from "./context/AuthContext";
import SocketProvider from "./context/SocketContext";
import { LoadingProvider, useLoading } from "./context/LoadingContext";
import { Toaster } from "sonner";

import Header from "./sections/Header";
import CookieConsentBanner from "./components/CookieConsentBanner";
import Footer from "./sections/Footer";
import AIChatWidget from "./components/AIChatWidget";
import Home from "./pages/Home";
import CoursesPage from "./pages/CoursesPage";
import MentorshipPage from "./pages/MentorshipPage";
import MentorshipSessionDetailPage from "./pages/MentorshipSessionDetailPage";
import MentorDetailPage from "./pages/MentorDetailPage";
import SearchPage from "./pages/SearchPage";
import StudentMentorshipSessionsPage from "./pages/StudentMentorshipSessionsPage";
import MentorMentorshipSessionsPage from "./pages/MentorMentorshipSessionsPage";
import MentorshipMessagesPage from "./pages/MentorshipMessagesPage";
import MentorSchedulePage from "./pages/MentorSchedulePage";
import MentorProfilePage from "./pages/MentorProfilePage";
import SessionReviewPage from "./pages/SessionReviewPage";
import AboutPage from "./pages/AboutPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import GoogleCallbackPage from "./pages/GoogleCallbackPage";
import ContactPage from "./pages/ContactPage";
import CourseDetailPage from "./pages/CourseDetailPage";
import CourseLearningPage from "./pages/CourseLearningPage";
import ProfilePage from "./pages/ProfilePage";
import ForumsPage from "./pages/ForumsPage";
import ForumDetailPage from "./pages/ForumDetailPage";
import CreateAccountPage from "./pages/RegisterPage";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import VerifyCertificatePage from "./pages/VerifyCertificatePage";
import BlogPage from "./pages/BlogPage";
import BlogDetailPage from "./pages/BlogDetailPage";
import CareerPage from "./pages/CareerPage";
import AdminMentorshipSessionsPage from "./pages/AdminMentorshipSessionsPage";
import AdminApplicationsPage from "./pages/AdminApplicationsPage";
import AdminEnrollmentTrends from "./pages/AdminEnrollmentTrends";
import AdminContactPage from "./pages/AdminContactPage";
import AdminActivityLogsPage from "./pages/AdminActivityLogsPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import AdminCoursesPage from "./pages/AdminCoursesPage";
import MentorDashboardPage from "./pages/MentorDashboardPage";
import AdminSettingsPage from "./pages/AdminSettingsPage";
import AdminCouponsPage from "./pages/AdminCouponsPage";
import BulkOperationsPage from "./pages/BulkOperationsPage";
import UserSettingsPage from "./pages/UserSettingsPage";
import CreateCoursePage from "./pages/CreateCoursePage";
import DocsPage from "./pages/DocsPage";
import UpdateCoursePage from "./pages/UpdateCoursePage";
import InstructorCoursesPage from "./pages/InstructorCoursesPage";
import InstructorAnalyticsPage from "./pages/InstructorAnalyticsPage";
import InstructorStudentsPage from "./pages/InstructorStudentsPage";
import CourseModulesPage from "./pages/CourseModulesPage";
import CourseFinishPage from "./pages/CourseFinishPage";
import CertificateExamPage from "./pages/CertificateExamPage";
import CreateCertificateExamPage from "./pages/CreateCertificateExamPage";
import CertificateViewPage from "./pages/CertificateViewPage";
import TakeQuizPage from "./pages/TakeQuizPage";
import WishlistPage from "./pages/WishlistPage";
// Dashboard imports
import DashboardLayout from "./components/dashboard/DashboardLayout";
import DashboardStudent from "./components/dashboard/DashboardStudent";
import DashboardMentor from "./components/dashboard/DashboardMentor";
import DashboardInstructor from "./components/dashboard/DashboardInstructor";
import DashboardAdmin from "./components/dashboard/DashboardAdmin";

import StudentSchedule from "./components/dashboard/student/StudentSchedule";
import StudentMessages from "./components/dashboard/student/StudentMessages";
import StudentCertificates from "./components/dashboard/student/StudentCertificates";
import StudentCourses from "./components/dashboard/student/StudentCourses";
import StudentQuizzes from "./components/dashboard/student/StudentQuizzes";
import StudentEvaluationPage from "./pages/StudentEvaluationPage";
import NotificationsPage from "./pages/NotificationsPage";
import BadgesPage from "./pages/BadgesPage";
import LeaderboardPage from "./pages/LeaderboardPage";
import VideoRoomPage from "./pages/VideoRoomPage";
import ProtectedRoute from "./components/ProtectedRoute";
import RoleProtectedRoute from "./components/RoleProtectedRoute";
import { useAuth } from "./context/AuthContext";
import AdminCourseContentPage from "./pages/AdminCourseContentPage";
import InstructorReviewsPage from "./pages/InstructorReviewsPage";
import EnrollmentSuccessPage from "./pages/EnrollmentSuccessPage";
import MentorshipSessionSuccessPage from "./pages/MentorshipSessionSuccessPage";
import LoadingSpinner from "./components/LoadingSpinner";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import MentorshipPaymentSuccessPage from "./pages/MentorshipPaymentSuccessPage";
import NotFoundPage from "./NotFoundPage";
import AIChatPage from "./pages/AIChatPage";
import AdminTransactionsPage from "./components/dashboard/admin/AdminTransactionsPage";


gsap.registerPlugin(ScrollTrigger);

// Scroll to top on route change
const ScrollToTop = () => {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    ScrollTrigger.refresh();
  }, [location]);
  return null;
};

const RouteChangeLoader = () => {
  const location = useLocation();
  const { showLoading, hideLoading, updateProgress } = useLoading();

  useEffect(() => {
    let progress = 0;
    let progressInterval: NodeJS.Timeout;
    let hideTimeout: NodeJS.Timeout;

    // Reset and show loader
    updateProgress(0);
    showLoading("Loading page...");

    // Animate progress
    progressInterval = setInterval(() => {
      if (progress < 90) {
        progress += Math.random() * 15;
        if (progress > 90) progress = 90;
        updateProgress(Math.floor(progress));
      }
    }, 100);

    // Hide after 1 second
    hideTimeout = setTimeout(() => {
      clearInterval(progressInterval);
      updateProgress(100);

      // Small delay for smooth transition
      setTimeout(() => {
        hideLoading();
      }, 150);
    }, 1000);

    return () => {
      clearInterval(progressInterval);
      clearTimeout(hideTimeout);
      hideLoading();
    };
  }, [location.pathname]);

  return null;
};

// Public layout — Header + Footer (skipped on /learn pages)
const PublicLayout = () => {
  const location = useLocation();
  // Skip header/footer on learn pages
  if (location.pathname.includes("/learn")) return <Outlet />;
  // Skip header/footer on finish, certificate-exam, and take-quiz pages
  if (
    location.pathname.includes("/finish") ||
    location.pathname.includes("/certificate-exam") ||
    location.pathname.includes("/take-quiz") ||
    location.pathname.includes("/certificate")
  )
    return <Outlet />;
  // Skip header/footer on auth pages (register/login)
  if (location.pathname === "/register" || location.pathname === "/login")
    return <Outlet />;
  // Hide header on messages page
  if (location.pathname.includes("/messages")) return <Outlet />;

  // Only show Footer on home page
  const showFooter =
    location.pathname === "/" ||
    location.pathname === "/home" ||
    location.pathname === "/search" ||
    location.pathname === "/courses" ||
    location.pathname === "/about" ||
    location.pathname === "/docs" ||
    location.pathname === "/blog" ||
    location.pathname === "/careers" ||
    location.pathname === "/contact" ||
    location.pathname === "/mentorship";

  return (
    <>
      <RouteChangeLoader />
      <Header />
      <Outlet />
      {showFooter && <Footer />}
      <CookieConsentBanner />
    </>
  );
};

// Auth layout — No Header/Footer, just the auth pages
const AuthLayout = () => {
  return (
    <>
      <RouteChangeLoader />
      <Outlet />
    </>
  );
};

// Clean layout — no Header, no Footer (used for forum detail, etc.)
const CleanLayout = () => {
  return (
    <>
      <RouteChangeLoader />
      <Outlet />
    </>
  );
};

// Dashboard layout wrapper
const DashboardLayoutWrapper = ({ userRole }: { userRole: string }) => {
  return (
    <>
      <RouteChangeLoader />
      <DashboardLayout userRole={userRole as any} />
    </>
  );
};

// Simple error boundary component
import { Component, ReactNode } from "react";
import TwoFactorPendingPage from "./pages/TwoFactorPendingPage";
import AdminMentorsPage from "./pages/AdminMentorsPage";
import StudentTransactionsPage from "./components/dashboard/student/StudentTransactionsPage";

interface ErrorBoundaryProps {
  children?: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Error caught by boundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <h1 className="mb-2 text-xl font-bold text-gray-900">
                Something went wrong
              </h1>
              <p className="mb-4 text-sm text-gray-600">
                Please try refreshing the page
              </p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 text-white rounded-lg bg-primary hover:bg-primary/90"
              >
                Reload page
              </button>
            </div>
          </div>
        )
      );
    }
    return this.props.children;
  }
}

// App content that uses AuthContext - with loading state handling
const AppContent = () => {
  const { user, isLoading: authLoading } = useAuth();

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const userRole = user?.role?.toLowerCase() || "student";

  return (
    <Router>
      <ScrollToTop />
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: "#fff",
            color: "#000",
            border: "1px solid #e5e7eb",
          },
          success: {
            style: {
              background: "#22c55e",
              color: "#fff",
              border: "none",
            },
          },
          error: {
            style: {
              background: "#ef4444",
              color: "#fff",
              border: "none",
            },
          },
        }}
      />
      <div className="relative min-h-screen bg-white">
        <Routes>
          {/* ── Public routes (with Header + Footer) ──────────────── */}
          <Route element={<PublicLayout />}>
            <Route path="/search" element={<SearchPage />} />
            <Route path="/" element={<Home />} />
            <Route path="/courses" element={<CoursesPage />} />
            <Route path="/course/:id" element={<CourseDetailPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/docs" element={<DocsPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:id" element={<BlogDetailPage />} />
            <Route path="/careers" element={<CareerPage />} />
            <Route path="/career" element={<CareerPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/wishlist" element={<WishlistPage />} />
          </Route>

          {/* Protected routes - require login */}
          <Route element={<ProtectedRoute />}>
            <Route element={<CleanLayout />}>
              <Route
                path="/video-room/:sessionId"
                element={<VideoRoomPage />}
              />
              <Route path="/forums/:forumId" element={<ForumDetailPage />} />
              <Route
                path="/forums/:forumId/:discussionId"
                element={<ForumDetailPage />}
              />
            </Route>
            <Route element={<PublicLayout />}>
              <Route path="/profile" element={<ProfilePage />} />
              <Route
                path="/certificate"
                element={
                  <RoleProtectedRoute
                    allowedRoles={["student", "mentor", "instructor", "admin"]}
                  >
                    <CertificateViewPage />
                  </RoleProtectedRoute>
                }
              />
              <Route path="/course/:id/finish" element={<CourseFinishPage />} />
              <Route
                path="/course/:id/certificate-exam"
                element={<CertificateExamPage />}
              />
              <Route
                path="/course/:courseId/module/:moduleId/take-quiz"
                element={<TakeQuizPage />}
              />
              <Route
                path="/course/:id/learn"
                element={<CourseLearningPage />}
              />
              <Route path="/mentorship" element={<MentorshipPage />} />
              <Route
                path="/mentorship/:mentorId"
                element={<MentorDetailPage />}
              />
              <Route
                path="/mentorship/sessions/:sessionId/review"
                element={<SessionReviewPage />}
              />
              <Route
                path="/mentorship/sessions/:sessionId"
                element={<MentorshipSessionDetailPage />}
              />
              <Route
                path="/dashboard/student/mentorship-sessions/:sessionId"
                element={
                  <RoleProtectedRoute allowedRoles={["student"]}>
                    <MentorshipSessionDetailPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/dashboard/mentor/sessions/:sessionId"
                element={
                  <RoleProtectedRoute allowedRoles={["mentor"]}>
                    <MentorshipSessionDetailPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/dashboard/admin/sessions/:sessionId"
                element={
                  <RoleProtectedRoute allowedRoles={["admin"]}>
                    <MentorshipSessionDetailPage />
                  </RoleProtectedRoute>
                }
              />
              <Route path="/forums" element={<ForumsPage />} />
            </Route>
            <Route element={<CleanLayout />}>
              <Route path="/leaderboard" element={<LeaderboardPage />} />
              <Route path="/ai-chat" element={<AIChatPage />} />
            </Route>
          </Route>

          {/* ── Auth routes (no Header/Footer) ────────────────────── */}
          <Route element={<AuthLayout />}>
            <Route path="/register" element={<CreateAccountPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
          </Route>

          {/* ── Clean routes (no Header, no Footer) ───────────────── */}
          <Route element={<CleanLayout />}>
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route
              path="/auth/google/callback"
              element={<GoogleCallbackPage />}
            />
            <Route
              path="/auth/2fa/pending"
              element={<TwoFactorPendingPage />}
            />
            <Route
              path="/verify-certificate/:certificateId"
              element={<VerifyCertificatePage />}
            />
            <Route
              path="/payment/success/:courseId"
              element={
                <RoleProtectedRoute allowedRoles={["student"]}>
                  <PaymentSuccessPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/payment/success"
              element={
                <RoleProtectedRoute allowedRoles={["student"]}>
                  <PaymentSuccessPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/payment-success"
              element={
                <RoleProtectedRoute allowedRoles={["student"]}>
                  <PaymentSuccessPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/mentorship-payment-success"
              element={
                <RoleProtectedRoute allowedRoles={["student"]}>
                  <MentorshipPaymentSuccessPage />
                </RoleProtectedRoute>
              }
            />
          </Route>

          {/* ── Dashboard routes (DashboardLayout, no Header/Footer) ─ */}
          <Route element={<DashboardLayoutWrapper userRole={userRole} />}>
            {/* Main dashboards */}
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route
              path="/dashboard/badges"
              element={
                <RoleProtectedRoute
                  allowedRoles={["student", "mentor", "instructor"]}
                >
                  <BadgesPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/student"
              element={
                <RoleProtectedRoute allowedRoles={["student"]}>
                  <DashboardStudent />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/mentor"
              element={
                <RoleProtectedRoute allowedRoles={["mentor"]}>
                  <DashboardMentor />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/instructor"
              element={
                <RoleProtectedRoute allowedRoles={["instructor"]}>
                  <DashboardInstructor />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/admin"
              element={
                <RoleProtectedRoute allowedRoles={["admin"]}>
                  <DashboardAdmin />
                </RoleProtectedRoute>
              }
            />

            {/* Student routes */}
            <Route
              path="/dashboard/student/courses"
              element={
                <RoleProtectedRoute allowedRoles={["student"]}>
                  <StudentCourses />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/student/quizzes"
              element={
                <RoleProtectedRoute allowedRoles={["student"]}>
                  <StudentQuizzes />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/student/certificates"
              element={
                <RoleProtectedRoute allowedRoles={["student"]}>
                  <StudentCertificates />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/student/schedule"
              element={
                <RoleProtectedRoute allowedRoles={["student"]}>
                  <StudentSchedule />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/student/messages"
              element={
                <RoleProtectedRoute allowedRoles={["student"]}>
                  <StudentMessages />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/student/evaluation"
              element={
                <RoleProtectedRoute allowedRoles={["student"]}>
                  <StudentEvaluationPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/student/forums"
              element={
                <RoleProtectedRoute allowedRoles={["student"]}>
                  <ForumsPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/student/forums/:forumId"
              element={
                <RoleProtectedRoute allowedRoles={["student"]}>
                  <ForumDetailPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/student/forums/:forumId/:discussionId"
              element={
                <RoleProtectedRoute allowedRoles={["student"]}>
                  <ForumDetailPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/student/wishlist"
              element={
                <RoleProtectedRoute allowedRoles={["student"]}>
                  <WishlistPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/enrollment-success/:courseId"
              element={
                <RoleProtectedRoute allowedRoles={["student"]}>
                  <EnrollmentSuccessPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/mentorship-success/:sessionId"
              element={
                <RoleProtectedRoute allowedRoles={["student"]}>
                  <MentorshipSessionSuccessPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/student/analytics"
              element={
                <RoleProtectedRoute allowedRoles={["student"]}>
                  <div className="p-4">
                    <h2 className="text-lg font-bold">Analytics Page</h2>
                    <p className="text-sm text-gray-500">Coming soon...</p>
                  </div>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/student/mentorship-sessions"
              element={
                <RoleProtectedRoute allowedRoles={["student"]}>
                  <StudentMentorshipSessionsPage />
                </RoleProtectedRoute>
              }
            />

            {/* Mentor routes */}
            <Route
              path="/dashboard/mentor"
              element={
                <RoleProtectedRoute allowedRoles={["mentor"]}>
                  <MentorDashboardPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/mentor/mentees"
              element={
                <RoleProtectedRoute allowedRoles={["mentor"]}>
                  <MentorDashboardPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/mentor/sessions"
              element={
                <RoleProtectedRoute allowedRoles={["mentor"]}>
                  <MentorMentorshipSessionsPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/mentor/profile"
              element={
                <RoleProtectedRoute allowedRoles={["mentor"]}>
                  <MentorProfilePage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/mentor/schedule"
              element={
                <RoleProtectedRoute allowedRoles={["mentor"]}>
                  <MentorSchedulePage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/mentor/messages"
              element={
                <RoleProtectedRoute allowedRoles={["mentor"]}>
                  <MentorshipMessagesPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/mentor/earnings"
              element={
                <RoleProtectedRoute allowedRoles={["mentor"]}>
                  <div className="p-4">
                    <h2 className="text-lg font-bold">Earnings Page</h2>
                    <p className="text-sm text-gray-500">Coming soon...</p>
                  </div>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/mentor/analytics"
              element={
                <RoleProtectedRoute allowedRoles={["mentor"]}>
                  <div className="p-4">
                    <h2 className="text-lg font-bold">Analytics Page</h2>
                    <p className="text-sm text-gray-500">Coming soon...</p>
                  </div>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/mentor/resources"
              element={
                <RoleProtectedRoute allowedRoles={["mentor"]}>
                  <div className="p-4">
                    <h2 className="text-lg font-bold">Resources Page</h2>
                    <p className="text-sm text-gray-500">Coming soon...</p>
                  </div>
                </RoleProtectedRoute>
              }
            />

            {/* Instructor routes */}
            <Route
              path="/dashboard/instructor/courses"
              element={
                <RoleProtectedRoute allowedRoles={["instructor"]}>
                  <InstructorCoursesPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/instructor/create-course"
              element={
                <RoleProtectedRoute allowedRoles={["instructor"]}>
                  <CreateCoursePage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/instructor/courses/:id"
              element={
                <RoleProtectedRoute allowedRoles={["instructor", "admin"]}>
                  <UpdateCoursePage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/instructor/courses/:id/edit"
              element={
                <RoleProtectedRoute allowedRoles={["instructor", "admin"]}>
                  <UpdateCoursePage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/instructor/courses/:id/modules"
              element={
                <RoleProtectedRoute allowedRoles={["instructor", "admin"]}>
                  <CourseModulesPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/admin/transactions"
              element={
                <RoleProtectedRoute allowedRoles={["instructor", "admin"]}>
                  <AdminTransactionsPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/instructor/certificate-exam"
              element={
                <RoleProtectedRoute allowedRoles={["instructor", "admin"]}>
                  <CreateCertificateExamPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/instructor/certificate-exam/:courseId"
              element={
                <RoleProtectedRoute allowedRoles={["instructor", "admin"]}>
                  <CreateCertificateExamPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/instructor/students"
              element={
                <RoleProtectedRoute allowedRoles={["instructor"]}>
                  <InstructorStudentsPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/instructor/analytics"
              element={
                <RoleProtectedRoute allowedRoles={["instructor"]}>
                  <InstructorAnalyticsPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/instructor/revenue"
              element={
                <RoleProtectedRoute allowedRoles={["instructor"]}>
                  <div className="p-4">
                    <h2 className="text-lg font-bold">Revenue Page</h2>
                    <p className="text-sm text-gray-500">Coming soon...</p>
                  </div>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/instructor/reviews"
              element={
                <RoleProtectedRoute allowedRoles={["instructor"]}>
                  <InstructorReviewsPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/instructor/messages"
              element={
                <RoleProtectedRoute allowedRoles={["instructor"]}>
                  <div className="p-4">
                    <h2 className="text-lg font-bold">Messages Page</h2>
                    <p className="text-sm text-gray-500">Coming soon...</p>
                  </div>
                </RoleProtectedRoute>
              }
            />

            {/* Admin routes */}
            <Route
              path="/dashboard/admin/users"
              element={
                <RoleProtectedRoute allowedRoles={["admin"]}>
                  <AdminUsersPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/admin/sessions"
              element={
                <RoleProtectedRoute allowedRoles={["admin"]}>
                  <AdminMentorshipSessionsPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/admin/create-course"
              element={
                <RoleProtectedRoute allowedRoles={["admin", "instructor"]}>
                  <CreateCoursePage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/admin/courses"
              element={
                <RoleProtectedRoute allowedRoles={["admin"]}>
                  <AdminCoursesPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/admin/courses/:id/modules"
              element={
                <RoleProtectedRoute allowedRoles={["admin"]}>
                  <CourseModulesPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/admin/courses/:id/content"
              element={
                <RoleProtectedRoute allowedRoles={["admin"]}>
                  <AdminCourseContentPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/admin/applications"
              element={
                <RoleProtectedRoute allowedRoles={["admin"]}>
                  <AdminApplicationsPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/admin/contacts"
              element={
                <RoleProtectedRoute allowedRoles={["admin"]}>
                  <AdminContactPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/admin/activity-logs"
              element={
                <RoleProtectedRoute allowedRoles={["admin"]}>
                  <AdminActivityLogsPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/admin/coupons"
              element={
                <RoleProtectedRoute allowedRoles={["admin"]}>
                  <AdminCouponsPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/admin/certificate-exam"
              element={
                <RoleProtectedRoute allowedRoles={["admin"]}>
                  <CreateCertificateExamPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/admin/certificate-exam/:courseId"
              element={
                <RoleProtectedRoute allowedRoles={["admin"]}>
                  <CreateCertificateExamPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/admin/students"
              element={
                <RoleProtectedRoute allowedRoles={["admin"]}>
                  <AdminUsersPage filterRole="student" />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/student/transactions"
              element={
                <RoleProtectedRoute allowedRoles={["student"]}>
                  <StudentTransactionsPage />
                </RoleProtectedRoute>
              }
            />

            <Route
              path="/dashboard/admin/settings"
              element={
                <RoleProtectedRoute allowedRoles={["admin"]}>
                  <AdminSettingsPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/admin/bulk-operations"
              element={
                <RoleProtectedRoute allowedRoles={["admin"]}>
                  <BulkOperationsPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/admin/instructors"
              element={
                <RoleProtectedRoute allowedRoles={["admin"]}>
                  <AdminUsersPage filterRole="instructor" />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/admin/mentors"
              element={
                <RoleProtectedRoute allowedRoles={["admin"]}>
                  <AdminMentorsPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/admin/revenue"
              element={
                <RoleProtectedRoute allowedRoles={["admin"]}>
                  <div className="p-4">
                    <h2 className="text-lg font-bold">Revenue Analytics</h2>
                    <p className="text-sm text-gray-500">Coming soon...</p>
                  </div>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/admin/reports"
              element={
                <RoleProtectedRoute allowedRoles={["admin"]}>
                  <div className="p-4">
                    <h2 className="text-lg font-bold">Reports Page</h2>
                    <p className="text-sm text-gray-500">Coming soon...</p>
                  </div>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/admin/enrollment-trends"
              element={
                <RoleProtectedRoute allowedRoles={["admin"]}>
                  <AdminEnrollmentTrends />
                </RoleProtectedRoute>
              }
            />

            {/* Common dashboard routes */}
            <Route
              path="/settings"
              element={
                <RoleProtectedRoute
                  allowedRoles={["student", "mentor", "instructor", "admin"]}
                >
                  <UserSettingsPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/dashboard/support"
              element={
                <div className="p-4">
                  <h2 className="text-lg font-bold">Help & Support</h2>
                  <p className="text-sm text-gray-500">Coming soon...</p>
                </div>
              }
            />
          </Route>
          <Route path="/notifications" element={<NotificationsPage />} />
          {/* ── 404 Not Found Route ─────────────────────────────────── */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </div>

      {/* AI Chat Widget - Floating in bottom right */}
      <AIChatWidget />
    </Router>
  );
};

// Main App component with all providers
function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <SocketProvider>
          <LoadingProvider>
            <AppContent />
          </LoadingProvider>
        </SocketProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
